
import { useState } from 'react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Trash2, AlertTriangle } from 'lucide-react';

interface DeleteProviderDialogProps {
  onDelete: () => Promise<void>;
  providerName: string;
  disabled?: boolean;
}

export const DeleteProviderDialog = ({ 
  onDelete, 
  providerName,
  disabled = false
}: DeleteProviderDialogProps) => {
  const [open, setOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const handleDelete = async () => {
    try {
      setIsDeleting(true);
      console.log(`Starting deletion process for provider: "${providerName}"`);
      
      await onDelete();
      
      console.log(`Successfully initiated deletion for provider: "${providerName}"`);
      setOpen(false);
    } catch (error) {
      console.error(`Error deleting provider "${providerName}":`, error);
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <>
      <Button 
        variant="destructive" 
        onClick={() => setOpen(true)}
        disabled={disabled}
        type="button" // Prevent form submission
      >
        <Trash2 className="mr-2 h-4 w-4" />
        Delete
      </Button>
      
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete PEO Provider</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete <strong>{providerName}</strong>?
              This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          
          {isDeleting && (
            <div className="flex items-center justify-center text-amber-600">
              <AlertTriangle className="h-5 w-5 mr-2" />
              <p>Deleting provider...</p>
            </div>
          )}
          
          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => setOpen(false)} 
              disabled={isDeleting}
              type="button"
            >
              Cancel
            </Button>
            <Button 
              variant="destructive" 
              onClick={handleDelete} 
              disabled={isDeleting}
              type="button"
            >
              {isDeleting ? (
                <>
                  <span className="mr-2">Deleting...</span>
                </>
              ) : (
                'Delete'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};
